// Görselli "Nasıl kullanılır" penceresi. Ekranın küçük bir haritası çizilir; her adımda ilgili bölge vurgulanır,
// haritada bir bölgeye tıklayınca o bölgenin ilk adımına gidilir. Haritanın şekli `layout` ile seçilir
// (kart, liste, rapor, pencere, sekmeli pencere, liste + form). `layout: 'chart'` adımsız tek sayfadır:
// grafik taslağı ve renk/durum açıklamaları gösterilir.
import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MobileStepper, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useTranslation } from 'react-i18next';
import { LAYOUT_GRID, LAYOUT_REGIONS, drawRegion, type GuideLayout } from './guide/guideLayouts';

export type { GuideLayout } from './guide/guideLayouts';

export interface GuideStep {
  /** Haritada vurgulanacak bölge (düzenin bölge anahtarlarından biri). */
  region: string;
  title: string;
  text: string;
  tips?: string[];
}

export interface GuideChartLegend {
  color: string;
  label: string;
  text: string;
}

export interface GuideChart {
  kind: 'bar' | 'stacked' | 'pie' | 'line';
  legend: GuideChartLegend[];
  /** Açıklamanın altında gösterilen kapanış notu. */
  note?: string;
}

/** Bir rehberin içeriği; rehber kaydındaki içerik üreticileri bunu döndürür. */
export interface GuideContent {
  title: string;
  intro?: string;
  layout?: GuideLayout | 'chart';
  /** Bölge adlarını ekrana göre değiştirmek için (ör. strip: "Kimlik şeridi"). */
  regionLabels?: Partial<Record<string, string>>;
  /** Kart düzeninde sol bölüm adları, sekmeli düzende sekme adları. */
  navItems?: string[];
  /** Haritayı yatayda ayna yap (ör. split düzeninde form solda, liste sağdaysa). */
  mirror?: boolean;
  steps?: GuideStep[];
  chart?: GuideChart;
}

export interface GuideLabels {
  back: string;
  next: string;
  done: string;
  close: string;
  stepOf: (n: number, total: number) => string;
}

export interface UsageGuideDialogProps extends GuideContent {
  open: boolean;
  onClose: () => void;
  labels?: Partial<GuideLabels>;
}

const ScreenMap: React.FC<{
  layout: GuideLayout;
  active: string;
  regionLabel: (r: string) => string;
  navItems: string[];
  stepNo: number;
  mirror?: boolean;
  onPick: (r: string) => void;
}> = ({ layout, active, regionLabel, navItems, stepNo, mirror, onPick }) => {
  const theme = useTheme();
  const base = LAYOUT_GRID[layout];
  // Ayna: her satırdaki alan sırası ve sütun genişlikleri ters çevrilir
  const grid = mirror
    ? {
        ...base,
        areas: base.areas.replace(/"([^"]*)"/g, (_, row: string) => `"${row.split(' ').reverse().join(' ')}"`),
        columns: base.columns.split(' ').reverse().join(' '),
      }
    : base;
  return (
    <Box
      data-testid={`guide-map-${layout}`}
      sx={{
        display: 'grid',
        gridTemplateAreas: grid.areas,
        gridTemplateColumns: grid.columns,
        gridTemplateRows: grid.rows,
        gap: 0.75,
        height: 290,
        p: 1,
        borderRadius: 1.5,
        bgcolor: 'action.hover',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {LAYOUT_REGIONS[layout].map(region => {
        const on = region === active;
        return (
          <Box
            key={region}
            component="button"
            type="button"
            onClick={() => onPick(region)}
            aria-label={regionLabel(region)}
            aria-current={on ? 'step' : undefined}
            sx={{
              all: 'unset',
              gridArea: region,
              position: 'relative',
              // Düğme içeriği varsayılan olarak dikey ortalanır; taslak üstten başlasın
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              textAlign: 'left',
              boxSizing: 'border-box',
              cursor: 'pointer',
              borderRadius: 1,
              border: '1.5px solid',
              borderColor: on ? 'primary.main' : 'divider',
              bgcolor: on ? alpha(theme.palette.primary.main, 0.12) : 'background.paper',
              boxShadow: on ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.18)}` : 'none',
              transition: 'background-color .2s, border-color .2s, box-shadow .2s',
              p: 0.75,
              minHeight: 0,
              overflow: 'hidden',
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
            }}
          >
            <Typography
              variant="caption"
              sx={{ display: 'block', fontWeight: on ? 700 : 500, color: on ? 'primary.main' : 'text.secondary', lineHeight: 1.3, pr: on ? 3 : 0 }}
              noWrap
            >
              {regionLabel(region)}
            </Typography>
            {drawRegion(layout, region, navItems)}
            {on && (
              <Box
                sx={{
                  position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%',
                  bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center',
                  fontSize: 11, fontWeight: 700,
                }}
              >
                {stepNo}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

/** Grafik rehberi için sade taslak: renkler açıklamadaki renklerle aynıdır. */
const ChartSketch: React.FC<{ chart: GuideChart }> = ({ chart }) => {
  const colors = chart.legend.map(l => l.color);
  const c = (i: number) => colors[i % Math.max(colors.length, 1)] || '#999';
  const W = 260;
  const H = 170;
  let body: React.ReactNode = null;
  if (chart.kind === 'pie') {
    const total = colors.length || 1;
    let acc = 0;
    body = colors.map((_, i) => {
      const a0 = (acc / total) * Math.PI * 2 - Math.PI / 2;
      acc += 1;
      const a1 = (acc / total) * Math.PI * 2 - Math.PI / 2;
      const r = 70;
      const cx = W / 2;
      const cy = H / 2;
      const large = a1 - a0 > Math.PI ? 1 : 0;
      const d = total === 1
        ? `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0`
        : `M ${cx} ${cy} L ${cx + r * Math.cos(a0)} ${cy + r * Math.sin(a0)} A ${r} ${r} 0 ${large} 1 ${cx + r * Math.cos(a1)} ${cy + r * Math.sin(a1)} Z`;
      return <path key={i} d={d} fill={c(i)} stroke="#fff" strokeWidth={2} />;
    });
  } else if (chart.kind === 'line') {
    const pts = [[20, 120], [70, 90], [120, 100], [170, 60], [240, 40]];
    body = colors.map((_, i) => (
      <polyline
        key={i}
        fill="none"
        stroke={c(i)}
        strokeWidth={3}
        points={pts.map(([x, y]) => `${x},${Math.min(H - 10, y + i * 25)}`).join(' ')}
      />
    ));
  } else {
    const groups = 5;
    const gw = (W - 30) / groups;
    body = Array.from({ length: groups }).map((_, g) => {
      const heights = colors.map((__, i) => 20 + ((g * 37 + i * 23) % 60));
      if (chart.kind === 'stacked') {
        let y = H - 10;
        return heights.map((h, i) => {
          y -= h * 0.6;
          return <rect key={`${g}-${i}`} x={20 + g * gw} y={y} width={gw * 0.6} height={h * 0.6} fill={c(i)} rx={2} />;
        });
      }
      const bw = (gw * 0.75) / Math.max(colors.length, 1);
      return heights.map((h, i) => (
        <rect key={`${g}-${i}`} x={20 + g * gw + i * bw} y={H - 10 - h * 1.6} width={bw - 1} height={h * 1.6} fill={c(i)} rx={2} />
      ));
    });
  }
  return (
    <Box
      data-testid="guide-chart"
      sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', display: 'grid', placeItems: 'center' }}
    >
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img" aria-hidden="true" style={{ maxWidth: 320 }}>
        {chart.kind !== 'pie' && <line x1={14} y1={H - 10} x2={W - 6} y2={H - 10} stroke="currentColor" strokeOpacity={0.25} />}
        {body}
      </svg>
    </Box>
  );
};

const UsageGuideDialog: React.FC<UsageGuideDialogProps> = ({
  open, onClose, title, intro, layout = 'card', regionLabels, navItems = [], mirror, steps = [], chart, labels: labelsIn,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('guides');
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [index, setIndex] = useState(0);

  useEffect(() => { if (open) setIndex(0); }, [open]);

  const labels: GuideLabels = {
    back: t('common.back', 'Geri'),
    next: t('common.next', 'İleri'),
    done: t('common.done', 'Anladım'),
    close: t('common.close', 'Kapat'),
    stepOf: (n, total) => t('common.stepOf', 'Adım {{n}} / {{total}}', { n, total }),
    ...labelsIn,
  };
  const regionLabel = (r: string) => regionLabels?.[r] ?? t(`common.regions.${r}`, r);

  const isChart = layout === 'chart';
  if (!isChart && steps.length === 0) return null;
  if (isChart && !chart) return null;

  const step = steps[Math.min(index, steps.length - 1)];
  const last = index >= steps.length - 1;

  // Haritada bir bölgeye tıklanınca o bölgenin ilk adımına gidilir
  const pick = (r: string) => {
    const i = steps.findIndex(s => s.region === r);
    if (i >= 0) setIndex(i);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Typography component="span" variant="h6" sx={{ flex: 1, fontWeight: 700 }}>{title}</Typography>
        <IconButton onClick={onClose} aria-label={labels.close}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {intro && (isChart || index === 0) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{intro}</Typography>
        )}
        {isChart && chart ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.1fr' }, gap: 3, alignItems: 'start' }}>
            <ChartSketch chart={chart} />
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 1.25 }}>
              {chart.legend.map(item => (
                <Box component="li" key={item.label} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: item.color, flex: 'none', mt: 0.4 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={700}>{item.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                  </Box>
                </Box>
              ))}
              {chart.note && (
                <Typography component="li" variant="body2" color="text.secondary" sx={{ pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  {chart.note}
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: 3, alignItems: 'start' }}>
            <ScreenMap
              layout={layout as GuideLayout}
              active={step.region}
              regionLabel={regionLabel}
              navItems={navItems}
              stepNo={index + 1}
              mirror={mirror}
              onPick={pick}
            />
            <Box aria-live="polite">
              <Typography variant="overline" color="text.secondary">{labels.stepOf(index + 1, steps.length)}</Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{step.title}</Typography>
              <Typography variant="body2" sx={{ mb: step.tips?.length ? 1.5 : 0 }}>{step.text}</Typography>
              {step.tips && step.tips.length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.5 }}>
                  {step.tips.map(tip => (
                    <Typography key={tip} component="li" variant="body2" color="text.secondary">{tip}</Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        {isChart ? (
          <Button size="small" variant="contained" onClick={onClose}>{labels.done}</Button>
        ) : (
          <MobileStepper
            variant="dots"
            steps={steps.length}
            position="static"
            activeStep={index}
            sx={{ flex: 1, bgcolor: 'transparent', p: 0 }}
            backButton={(
              <Button size="small" onClick={() => setIndex(i => i - 1)} disabled={index === 0} startIcon={<KeyboardArrowLeft />}>
                {labels.back}
              </Button>
            )}
            nextButton={last ? (
              <Button size="small" variant="contained" onClick={onClose}>{labels.done}</Button>
            ) : (
              <Button size="small" variant="contained" onClick={() => setIndex(i => i + 1)} endIcon={<KeyboardArrowRight />}>
                {labels.next}
              </Button>
            )}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UsageGuideDialog;
